// Palette fermée de 16 couleurs d'équipes (doc 05 §3.1), calibrées comme fond de
// ligne sur gray.950. `on` = couleur de texte, précalculée et stockée (doc 05 §3.2).
// Chaque équipe porte aussi une forme (daltonisme, doc 05 §3.3).

export interface TeamColor {
  key: string; // clé i18n : `team.${key}`
  hex: string;
  on: '#101014' | '#FFFFFF';
  shape: string; // assets/shapes/<shape>.svg (viewBox 24, fill="currentColor")
}

const NOIR = '#101014';
const BLANC = '#FFFFFF';

export const teamColors = [
  { key: 'red', hex: '#E5484D', on: BLANC, shape: 'circle' }, // 1  cercle plein
  { key: 'orange', hex: '#F76B15', on: NOIR, shape: 'triangle' }, // 2  triangle
  { key: 'yellow', hex: '#FACC15', on: NOIR, shape: 'star' }, // 3  étoile
  { key: 'chartreuse', hex: '#A3E635', on: NOIR, shape: 'lozenge' }, // 4  losange
  { key: 'green', hex: '#22C55E', on: NOIR, shape: 'square' }, // 5  carré
  { key: 'mint', hex: '#6EE7B7', on: NOIR, shape: 'drop' }, // 6  goutte
  { key: 'teal', hex: '#14B8A6', on: NOIR, shape: 'pentagon' }, // 7  pentagone
  { key: 'cyan', hex: '#22D3EE', on: NOIR, shape: 'cross' }, // 8  croix
  { key: 'sky', hex: '#38BDF8', on: NOIR, shape: 'crescent' }, // 9  demi-lune
  { key: 'blue', hex: '#3B82F6', on: BLANC, shape: 'hexagon' }, // 10 hexagone
  { key: 'indigo', hex: '#6366F1', on: BLANC, shape: 'bolt' }, // 11 éclair
  { key: 'violet', hex: '#8B5CF6', on: BLANC, shape: 'diamond' }, // 12 diamant
  { key: 'fuchsia', hex: '#D946EF', on: BLANC, shape: 'spark' }, // 13 étincelle
  { key: 'pink', hex: '#F472B6', on: NOIR, shape: 'heart' }, // 14 cœur
  { key: 'chalk', hex: '#E8E9EB', on: NOIR, shape: 'ring' }, // 15 anneau
  { key: 'steel', hex: '#94A3B8', on: NOIR, shape: 'wave' }, // 16 vague
] as const satisfies readonly TeamColor[];

export type TeamColorKey = (typeof teamColors)[number]['key'];

// Ordre d'attribution automatique (doc 05 §3.1) : maximise la distance perceptuelle
// des premières équipes — 1 → 10 → 3 → 12 → 5 → 8 → 2 → 14 → 7 → 11 → 4 → 16 → 6 → 13 → 9 → 15.
// Au-delà de 16, la couleur cycle (l'avatar + la forme différencient).
export const teamAssignOrder = [
  'red', // 1
  'blue', // 10
  'yellow', // 3
  'violet', // 12
  'green', // 5
  'cyan', // 8
  'orange', // 2
  'pink', // 14
  'teal', // 7
  'indigo', // 11
  'chartreuse', // 4
  'steel', // 16
  'mint', // 6
  'fuchsia', // 13
  'sky', // 9
  'chalk', // 15
] as const satisfies readonly TeamColorKey[];

export function teamColorByKey(key: TeamColorKey): TeamColor {
  const found = teamColors.find((c) => c.key === key);
  if (!found) {
    throw new Error(`Unknown team color key: ${key}`);
  }
  return found;
}

/** Couleur de la n-ième équipe créée (0-based), en cyclant au-delà de 16. */
export function teamColorForIndex(index: number): TeamColor {
  return teamColorByKey(teamAssignOrder[index % teamAssignOrder.length]);
}
